import { useCallback } from 'react';
import groupBy from 'lodash/groupBy';
import noop from 'lodash/noop';
import type { IssuesUpdatePayload } from '@atlassian/jira-bulk-operations-common/src/controllers/use-form-submit-state/types.tsx';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { ROOT_LEVEL } from '@atlassian/jira-business-list-handlers/src/common/constants.tsx';
import { useCacheActions } from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/main.tsx';
import type { BaseIssueNode } from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/types.tsx';
import { useUpdateIssueParent } from '@atlassian/jira-business-list-handlers/src/controllers/update-issue-parent/index.tsx';
import { useHierarchyStore } from '@atlassian/jira-business-list/src/controllers/hierarchy/hierarchy-store/index.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config';
import type {
	JWMListViewItemDetails_jira_list_JiraWorkManagementViewItemConnection_edges_node as JWMListViewItemDetailsNode,
	JWMListViewItemDetails,
} from '../../../../services/get-list-view-issues/__generated_apollo__/JWMListViewItemDetails';
import { LIST_VIEW_QUERY } from '../../../../services/get-list-view-issues/gql';
import type { ListViewIssuesReturn } from '../../../../services/get-list-view-issues/types';
import {
	addChildToParentListView,
	removeChildFromParentListView,
} from '../../../../services/get-list-view-issues/utils';
import { useIssueFields } from '../../../get-issue-fields';
import { useBulkSelectionStore } from '../../bulk-selection-store';
import { transformData } from './utils';

export const useBulkEditCacheActions = () => {
	const { updateItemsInCache } = useCacheActions();
	const [{ selectedIssues }] = useBulkSelectionStore();
	const [{ issuesMap, issueParentMap, childrenItems }] = useHierarchyStore();

	const { getQueryFieldIds } = ff('thor-3996-optimistic-update-fixes-for-parent-field_6zcxj')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIssueFields()
		: { getQueryFieldIds: noop };
	const queryFieldIds = ff('thor-3996-optimistic-update-fixes-for-parent-field_6zcxj')
		? getQueryFieldIds()
		: undefined;
	const { key: projectKey } = ff('thor-3996-optimistic-update-fixes-for-parent-field_6zcxj')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useProject()
		: { key: undefined };

	const { updateIssueParent, unlinkParentIssue } = ff(
		'thor-3996-optimistic-update-fixes-for-parent-field_6zcxj',
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useUpdateIssueParent<
				ListViewIssuesReturn,
				JWMListViewItemDetailsNode,
				JWMListViewItemDetails
			>({
				childrenQueryData: childrenItems,
				// @ts-expect-error - TS2322 - Type 'void | string[]' is not assignable to type 'string[]'.
				fieldIds: queryFieldIds,
				issuesMap,
				issueParentMap,
				cacheQuery: LIST_VIEW_QUERY,
				removeChildFromParent: removeChildFromParentListView<JWMListViewItemDetailsNode>,
				addChildToParent: addChildToParentListView<JWMListViewItemDetailsNode>,
				view: 'list',
				packageName: 'jiraBusinessList',
				shouldOnlyUpdateCache: true,
				projectKey,
			})
		: { updateIssueParent: noop, unlinkParentIssue: noop };

	const updateSelectedItemsInCache = useCallback(
		({ data, issueIds }: IssuesUpdatePayload) => {
			const selectedIssuesByParent = groupBy(
				ff('thor-3996-optimistic-update-fixes-for-parent-field_6zcxj')
					? [...issueIds.map((issueId) => issueId.toString())]
					: [...selectedIssues],
				(issueId) => issuesMap.get(issueId)?.parentIssueId || ROOT_LEVEL,
			);

			const parents = Object.entries(selectedIssuesByParent);

			const optimisticUpdateIssue = (existingIssue: BaseIssueNode) => {
				const updatedIssue = {
					...existingIssue,
					fields: [
						...(existingIssue.fields?.filter(
							(field) => !Object.keys(data).includes(field?.fieldId),
						) ?? []),
						...transformData(existingIssue, data),
					],
				};

				return updatedIssue;
			};

			parents.forEach(([, issues]) => {
				const variables = issuesMap.get(issues[0])?.cacheVariables;

				if (variables !== undefined) {
					updateItemsInCache({
						query: LIST_VIEW_QUERY,
						variables: { ...variables, withParentIssueId: fg('jsw_list_view_-_all_the_fields') },
						issueIds: issues,
						updateItem: optimisticUpdateIssue,
					});

					if (
						ff('thor-3996-optimistic-update-fixes-for-parent-field_6zcxj') &&
						data?.[PARENT_TYPE] !== undefined
					) {
						issues.forEach((issueId) => {
							if (data?.[PARENT_TYPE]?.id) {
								const parentIssue = issuesMap.get(data?.[PARENT_TYPE].id.toString());
								if (parentIssue) {
									updateIssueParent({
										issueKey: issueId,
										parentIssue,
									});
								}
							} else {
								unlinkParentIssue({
									issueKey: issueId,
								});
							}
						});
					}
				}
			});
		},
		[selectedIssues, issuesMap, updateItemsInCache, updateIssueParent, unlinkParentIssue],
	);

	return { updateSelectedItemsInCache };
};
